"use client";

import { Image as ImageIcon, MapPinIcon } from "lucide-react";

import { BASE_URL } from "@/lib/constants";
import { formatImageUrl, getProductV2Path } from "@/lib/utils";
import { useModal } from "@/providers/modal-provider";
import Image from "next/image";
import Link from "next/link";
import { ModalType } from "@/types/modal-type";
import { DaySpotlightProduct } from "../day-spotlight";

type TDaySpotLightCardProps = {
	index: number;
} & Pick<
	DaySpotlightProduct,
	"slug" | "name" | "tagline" | "logo" | "tags" | "isSponsored" | "hqLocation"
>;
export const DaySpotLightCard = ({
	index,
	slug,
	name,
	tagline,
	logo,
	tags,
	isSponsored,
	hqLocation,
}: TDaySpotLightCardProps) => {
	const { openModal } = useModal();

	const formattedLogoUrl = formatImageUrl(logo);
	const showProductDetailsModal = () => {
		try {
			const url = getProductV2Path(slug);
			const eventTitle = `Spotlight Product ${
				index === 0 ? "One" : index === 1 ? "Two" : "Three"
			} Clicked`;
			window.rudderanalytics?.track(eventTitle, {
				name: name,
				link: `${BASE_URL}${url}`,
				tagline: tagline,
				region: hqLocation,
				tags: tags,
				isSponsored: isSponsored,
			});

			history.pushState({}, "", url);

			openModal(ModalType.ProductDetails, { productSlug: slug });
		} catch (error) {
			console.log("Something went wrong while opening product modal");
		}
	};

	return (
		<div className="relative rounded-xl bg-white p-4 shadow-spotlight-card">
			<div className="flex items-start gap-2">
				<div className="flex aspect-square h-16 w-16 basis-20 items-center justify-center overflow-hidden rounded-lg border bg-transparent md:h-20 md:w-20 md:basis-28">
					{formattedLogoUrl ? (
						<Image src={formattedLogoUrl} alt={name} width={80} height={80} />
					) : (
						<ImageIcon className="h-5 w-5 text-gray-400 lg:h-8 lg:w-8" />
					)}
				</div>

				<div className="basis-full space-y-1.5">
					<button
						className="text-left font-bold text-gray-900 sm:text-lg"
						onClick={showProductDetailsModal}
					>
						{name}
					</button>

					<div className="hidden w-fit items-center gap-1 rounded bg-gray-50 p-1 text-xs text-gray-500 md:flex md:text-sm">
						<MapPinIcon className="h-3 w-3 text-blue-600" />
						{hqLocation}
					</div>

					<p
						title={tagline}
						className="text-sm text-gray-500 md:line-clamp-2 md:h-10 md:max-w-[200px]"
					>
						{tagline}
					</p>
				</div>
			</div>

			<div className="no-scrollbar mt-4 hidden w-full overflow-auto px-2 md:block">
				<div className="flex items-center gap-1">
					{tags.length > 0
						? tags.slice(0, 2).map((tag) => (
								<Link
									key={tag.id}
									href={`/tags/${tag.name}`}
									className="relative w-fit -skew-x-[8deg] whitespace-nowrap bg-brand-primary px-2 py-0.5 text-xs font-medium text-white"
									title={tag.name}
								>
									{tag.name}
								</Link>
						  ))
						: null}
				</div>
			</div>

			{isSponsored ? (
				<span className="absolute right-3 top-3 h-fit rounded-md bg-[#F9F8F8] px-2 py-1 text-xs text-[#A8A8A8]">
					Sponsored
				</span>
			) : null}
		</div>
	);
};
